import { memo, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiCard,
  MiCheckbox,
  MiDetailPageHeader,
  MiIcon,
  MiIconButton,
} from "@miplatform/components";
import { LABEL_CONFIG_AREAS, PERMISSIONS } from "@miplatform/constants";
import { mdiChevronLeft, mdiMinus, mdiPlus } from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Card, Fade, GridLegacy, Typography } from "@mui/material";
import {
  ORDER_FIELDS_CONFIG_TYPE,
  getGroupedFieldsConfig,
} from "./manageOrderCardFieldsConstants";
import { useManageOrderCardFieldsViewModel } from "./useManageOrderCardFieldsViewModel";

export const ManageOrderCardFields = () => {
  const { classes } = useStyles();
  const { hasPermission } = usePermissions();

  const {
    isLoading,
    isDirty,
    orderCardConfig,
    orderHoverCardConfig,
    orderMaxDisplayOrder,
    hoverMaxDisplayOrder,
    getConfig,
    handleFieldChange,
    handleDisplayOrderChange,
    handleReset,
    handleSubmit,
  } = useManageOrderCardFieldsViewModel();

  useEffect(() => {
    getConfig({ area: LABEL_CONFIG_AREAS.CARD });
    getConfig({ area: LABEL_CONFIG_AREAS.HOVER_CARD });
  }, []);

  const renderConfig = (config, maxDisplayOrder, configType) =>
    getGroupedFieldsConfig(config)?.map(({ section, fields }) => (
      <MiCard
        key={section}
        title={section}
        titleStyles={{ textTransform: "capitalize" }}
      >
        <GridLegacy container mb={6} gap={3}>
          {fields.map((field) => (
            <FieldCard
              key={field.fieldKey}
              isReadOnly={!hasPermission(PERMISSIONS.LABEL_CONFIG.UPDATE)}
              field={field}
              maxDisplayOrder={maxDisplayOrder}
              onChange={handleFieldChange}
              onDisplayOrderChange={handleDisplayOrderChange}
              configType={configType}
            />
          ))}
        </GridLegacy>
      </MiCard>
    ));

  return (
    <>
      <MiDetailPageHeader
        title="Order Card Fields"
        variant="underlined"
        titleIcon={mdiChevronLeft}
        loading={isLoading}
      />
      <MiBox className={classes.contentContainer}>
        {orderCardConfig && (
          <>
            <Typography className={classes.header}>
              Order Card Configurations
            </Typography>
            {renderConfig(
              orderCardConfig,
              orderMaxDisplayOrder,
              ORDER_FIELDS_CONFIG_TYPE.ORDER_CARD
            )}
          </>
        )}
        {orderHoverCardConfig && (
          <>
            <Typography className={classes.header}>
              Order Hover Card Configurations
            </Typography>
            {renderConfig(
              orderHoverCardConfig,
              hoverMaxDisplayOrder,
              ORDER_FIELDS_CONFIG_TYPE.ORDER_HOVER_CARD
            )}
          </>
        )}
      </MiBox>
      <Footer
        show={isDirty}
        handleSaveClick={handleSubmit}
        handleCancelClick={handleReset}
        disableSave={isLoading}
      />
    </>
  );
};

/**
 * FieldCard component for displaying a configurable field with display order controls and a checkbox.
 *
 * @component
 * @param {Object} props - Props passed to FieldCard.
 * @param {boolean} props.isReadOnly - If true, disables all interactions.
 * @param {Object} props.field - Field data containing properties like label, fieldKey, isSelected, and displayOrder.
 * @param {string} props.field.label - The label of the field shown in the checkbox.
 * @param {string} props.field.fieldKey - Unique identifier for the field.
 * @param {boolean} props.field.isSelected - Indicates whether the field is currently selected.
 * @param {number} [props.field.displayOrder] - The current display order of the field.
 * @param {number} props.maxDisplayOrder - The maximum allowable display order among all fields.
 * @param {Function} props.onChange - Callback when the checkbox selection changes.
 * @param {Function} props.onDisplayOrderChange - Callback when the display order is changed.
 * @param {string} props.configType - Type of configuration being edited (used in callbacks).
 *
 * @returns {JSX.Element} A card UI component displaying field selection and order controls.
 *
 */
const FieldCard = memo(
  ({
    isReadOnly,
    field,
    maxDisplayOrder,
    onChange,
    onDisplayOrderChange,
    configType,
  }) => {
    const { label, fieldKey, isSelected, displayOrder } = field;

    return (
      <Card sx={{ pr: 0.5, pb: 1, width: 200, maxWidth: 200 }}>
        <GridLegacy
          container
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Typography
            sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}
          >
            Display Order
          </Typography>
          <GridLegacy
            container
            width="fit-content"
            alignItems="center"
            height={30}
          >
            {!!displayOrder && isSelected && (
              <>
                <Fade in={displayOrder !== 1}>
                  <Box>
                    <MiIconButton
                      disabled={displayOrder === 1 || isReadOnly}
                      style={{ padding: 0 }}
                      onClick={() =>
                        onDisplayOrderChange(
                          fieldKey,
                          displayOrder,
                          displayOrder - 1,
                          configType
                        )
                      }
                    >
                      <MiIcon path={mdiMinus} />
                    </MiIconButton>
                  </Box>
                </Fade>
                <Typography>{displayOrder || 0}</Typography>
                <Fade in={displayOrder !== maxDisplayOrder}>
                  <Box>
                    <MiIconButton
                      disabled={displayOrder === maxDisplayOrder || isReadOnly}
                      style={{ padding: 0 }}
                      onClick={() =>
                        onDisplayOrderChange(
                          fieldKey,
                          displayOrder,
                          displayOrder + 1,
                          configType
                        )
                      }
                    >
                      <MiIcon path={mdiPlus} />
                    </MiIconButton>
                  </Box>
                </Fade>
              </>
            )}
          </GridLegacy>
        </GridLegacy>
        <MiCheckbox
          value={isSelected}
          label={label}
          disabled={isReadOnly}
          handleChange={({ target: { checked } }) =>
            onChange(fieldKey, checked, displayOrder, configType)
          }
        />
      </Card>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  header: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.fontWeight.semiBold,
    margin: theme.spacing(0, 0, 3, 4),
  },
  sectionHeader: {
    textTransform: "capitalize",
    fontWeight: theme.fontWeight.medium,
    marginBottom: theme.spacing(2),
  },
  contentContainer: {
    padding: theme.spacing(6, 3, 6, 3),
  },
}));
